/**
 * usePasskeyHint — "this browser has a passkey" remembrance.
 *
 * Stores a small, capped list of `{ email, name, ts }` in localStorage so the
 * login page can offer a fast passwordless passkey sign-in for accounts that
 * have previously used (or registered) a passkey on this device.
 *
 * SECURITY (load-bearing): the hint holds NO secrets, tokens, or credential
 * material — only an email, a display name, and a timestamp. It grants zero
 * privilege; all real authentication is still the WebAuthn ceremony (with user
 * verification) plus the server-side assertion check against the configured RP
 * id / origins. An attacker reading localStorage learns only an email address.
 *
 * Stored OUTSIDE the `pf_` namespace deliberately: `clearAllPersistedState()`
 * (run on logout) only wipes `pf_*` keys, and these hints are meant to survive
 * logout. It therefore joins the documented non-`pf_` exceptions (auth tokens,
 * cached user, theme prefs, …).
 *
 * The parser is defensive on purpose: tampered, oversized, or malformed entries
 * are dropped rather than thrown, so a corrupt localStorage value can never
 * break the login page.
 */

const STORAGE_KEY = 'passkeyHints';
const MAX_HINTS = 5;
const MAX_EMAIL_LENGTH = 320; // RFC 5321 maximum
const MAX_NAME_LENGTH = 120;

function normalizeEmail(email) {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

function sanitizeEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const email = normalizeEmail(entry.email);
  if (!email || email.length > MAX_EMAIL_LENGTH || !email.includes('@')) return null;
  const name =
    typeof entry.name === 'string' ? entry.name.trim().slice(0, MAX_NAME_LENGTH) : '';
  const ts = Number.isFinite(entry.ts) ? entry.ts : 0;
  return { email, name, ts };
}

/**
 * Read the validated, deduped, capped list of remembered passkey accounts.
 * Most-recent-first. Returns `[]` on any parse/storage error.
 *
 * @returns {Array<{ email: string, name: string, ts: number }>}
 */
export function readPasskeyHints() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const seen = new Set();
    const hints = [];
    for (const item of parsed) {
      const entry = sanitizeEntry(item);
      if (!entry || seen.has(entry.email)) continue;
      seen.add(entry.email);
      hints.push(entry);
      if (hints.length >= MAX_HINTS) break;
    }
    return hints;
  } catch {
    return [];
  }
}

function write(hints) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hints.slice(0, MAX_HINTS)));
  } catch {
    // ignore quota / private-mode failures
  }
}

/**
 * Upsert an account into the remembered list (move-to-front, refresh `ts`).
 * No-op when no valid email is supplied.
 *
 * @param {{ email?: string, name?: string }} account
 * @returns {Array<{ email: string, name: string, ts: number }>} the new list
 */
export function rememberPasskey({ email, name } = {}) {
  const entry = sanitizeEntry({ email, name, ts: Date.now() });
  if (!entry) return readPasskeyHints();

  const rest = readPasskeyHints().filter((hint) => hint.email !== entry.email);
  const next = [entry, ...rest].slice(0, MAX_HINTS);
  write(next);
  return next;
}

/**
 * Remove a single remembered account by email.
 *
 * @param {string} email
 * @returns {Array<{ email: string, name: string, ts: number }>} the new list
 */
export function forgetPasskey(email) {
  const target = normalizeEmail(email);
  if (!target) return readPasskeyHints();

  const next = readPasskeyHints().filter((hint) => hint.email !== target);
  if (next.length) {
    write(next);
  } else {
    clearPasskeyHints();
  }
  return next;
}

/** Remove all remembered passkey accounts for this browser. */
export function clearPasskeyHints() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** @returns {boolean} whether this browser has any remembered passkey account. */
export function hasPasskeyHints() {
  return readPasskeyHints().length > 0;
}

/**
 * @param {string} email
 * @returns {boolean} whether this browser has a remembered passkey for `email`.
 */
export function hasPasskeyHint(email) {
  const target = normalizeEmail(email);
  if (!target) return false;

  return readPasskeyHints().some((hint) => hint.email === target);
}
